import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

export function GoldenRules({ rules, compact }) {
  if (!rules?.length) return null;
  return (
    <div className="bg-white border-l-4 border-[#C9A227] border border-[#0B1F3A]/10 rounded-r-xl p-4 sm:p-5" data-testid="golden-rules">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#B8860B]">The golden rules</p>
      <div className={`mt-3 grid gap-x-6 gap-y-1.5 ${compact ? "" : "sm:grid-cols-2"}`}>
        {rules.map((r, i) => (
          <p key={r} className="text-xs text-[#0B1F3A] flex gap-2" data-testid={`golden-rule-${i + 1}`}>
            <span className="font-bold text-[#B8860B] shrink-0">{i + 1}.</span> {r}
          </p>
        ))}
      </div>
    </div>
  );
}

export default function DualTrackTimeline({ phases, plantStages, stageDetails, syncMap }) {
  const detailByStage = {};
  (stageDetails || []).forEach((s) => { detailByStage[s.name] = s.summary; });
  const gateByStage = {};
  (syncMap || []).forEach((s) => { gateByStage[s.plant_stage] = s; });
  const phaseByStage = {};
  (phases || []).forEach((p) => {
    phaseByStage[p.plant_stage] = phaseByStage[p.plant_stage] || [];
    phaseByStage[p.plant_stage].push(p);
  });

  return (
    <TooltipProvider delayDuration={150}>
      <div className="w-full" data-testid="dual-track-timeline">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#475569]">Two roadmaps, one project</p>
          <p className="text-[11px] text-[#475569]">Hover any stage or phase for what happens in it</p>
        </div>
        <div className="overflow-x-auto regnav-scroll pb-2 snap-x">
          <div className="min-w-[1040px]">
            <div className="flex items-center gap-1 mb-1">
              <span className="w-20 shrink-0 text-[10px] uppercase tracking-wider text-[#475569] font-semibold leading-tight">Roadmap 1<br />Plant build</span>
              {plantStages.map((s, i) => (
                <div key={s} className="flex-1 snap-start">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-[#1D4ED8] text-white rounded-md px-1.5 py-2 text-center text-[10.5px] font-medium leading-tight relative cursor-help h-full" data-testid={`plant-stage-${i + 1}`}>
                        <span className="block text-[9px] text-white/60 font-bold">{i + 1}</span>
                        {s}
                        {i < plantStages.length - 1 && <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 text-[#0B1F3A] z-10">›</span>}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-[#0B1F3A] text-[#F5F8FF] border-none text-xs">
                      <p className="font-semibold">{i + 1}. {s}</p>
                      {detailByStage[s] && <p className="mt-1 text-white/80">{detailByStage[s]}</p>}
                      {gateByStage[s]?.gate && <p className="mt-1.5 text-[#34D399]">Gate: {gateByStage[s].gate}</p>}
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}
            </div>
            <div className="flex gap-1 mb-1">
              <span className="w-20 shrink-0" />
              {plantStages.map((s) => (
                <div key={s} className="flex-1 flex justify-center">
                  {phaseByStage[s] ? <div className="h-5 border-l-2 border-dashed border-[#059669]" /> : <div className="h-5" />}
                </div>
              ))}
            </div>
            <div className="flex items-start gap-1">
              <span className="w-20 shrink-0 text-[10px] uppercase tracking-wider text-[#059669] font-semibold pt-2 leading-tight">Roadmap 2<br />Regulatory</span>
              {plantStages.map((s) => {
                const items = phaseByStage[s];
                return (
                  <div key={s} className="flex-1">
                    {items ? items.map((p) => {
                      const critical = p.approvals?.some((a) => a.critical);
                      return (
                        <Tooltip key={p.key}>
                          <TooltipTrigger asChild>
                            <div className={`rounded-md px-1.5 py-2 text-[10.5px] leading-tight border bg-white mb-1 cursor-help ${critical ? "border-[#059669] border-2 shadow-[0_0_0_2px_rgba(201,162,39,0.15)]" : "border-[#0B1F3A]/20"}`} data-testid={`timeline-phase-${p.key}`}>
                              <p className="font-bold text-[#B8860B] text-[9px]">{p.code}</p>
                              <p className="font-semibold text-[#0B1F3A]">{p.label}</p>
                              <p className="text-[#475569] mt-0.5">{p.approvals?.length} approval{p.approvals?.length !== 1 ? "s" : ""}</p>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs bg-[#0B1F3A] text-[#F5F8FF] border-none text-xs">
                            <p className="font-semibold">{p.code} · {p.label}</p>
                            <p className="mt-1 text-[#34D399]">{p.anchor}</p>
                            <p className="mt-1.5 text-white/80">{p.approvals?.map((a) => a.name).join(" · ")}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }) : <div className="rounded-md px-2 py-2 text-[10px] text-center text-[#475569]/50 border border-dashed border-[#0B1F3A]/10">—</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <p className="text-xs text-[#475569] mt-2 italic">
          Each regulatory phase must be complete before the plant stage above it. The roadmap nobody plans is the one that stops the plant.
        </p>
      </div>
    </TooltipProvider>
  );
}
